import { ApiClient } from '../client/ApiClient';
import { ApiResponse } from '../types';
import { AlgoliaProductHit } from './types/AlgoliaProductHit';
import { algoliasearch, Algoliasearch, SearchParamsObject } from 'algoliasearch';
import { createFetchRequester } from '@algolia/requester-fetch';
import { buildSearchFilters } from '@jbhi-fi/utils/src/buildAlgoliaSearchFilters/buildAlgoliaSearchFilters';
import { toCamelCase } from '@jbhi-fi/utils/src/toCamelCase';
import { createFallbackableCache } from '@algolia/cache-common';
import { createBrowserLocalStorageCache } from '@algolia/cache-browser-local-storage';
import { createInMemoryCache } from '@algolia/cache-in-memory';
import { ContentfulCollection } from '../contentful/types';
import {
  filterGoogleRecommendations,
  GoogleRecommendedItemWithVariance,
} from '../utils/googleRecommended/filterGoogleRecommendations';
import { GoogleSearchResponse } from './types/GoogleProductHit';

const STOP_BATCHING_AFTER_MS = 2000; // stop batching requests after 2 seconds

export type GetSkusResponse = {
  products: Array<{ id: string; sku: string }>;
};

export type GetProductsResponse = {
  products: Array<ScoredAlgoliaHit>;
  numberOfProducts?: number;
};

export type ScoredAlgoliaHit = {
  variance?: number;
  varianceScore?: number;
  googleScore?: number;
} & AlgoliaProductHit;

export type ProductClientConstructorArgs = {
  shopifyProductsGqlUrl: string;
  shopifyProductsGqlToken: string;
  algoliaAppId: string;
  algoliaSearchApiKey: string;
  algoliaSearchIndex: string;
  recommendationToken?: string;
  recommendationsEndpoint?: string;
  googleSearchEndpoint?: string;
  googleSearchApiKey?: string;
  shouldExcludeMarketplaceProductsFromSuggestions?: boolean;
  shouldUseBrowseApi?: boolean;
  batchedQueriesWaitTimeMs?: number;
};

export type GoogleSearchArgs = {
  googleSearchQuery?: string;
  visitorId?: string;
};

export type PartialProduct = {
  sku: string;
};

export type AlgoliaSearchOptions = Omit<SearchParamsObject, 'filters'>;

export type GetProductsOptions = {
  shouldResolveExtraOnlineProductFields?: boolean;
  shouldExcludeMarketplaceProducts?: boolean;
  sortByMethod?: Exclude<ContentfulCollection['fields'], undefined>['defaultSortMethod'];
  isPerks?: boolean;
  analyticsTags?: Array<string>;
  acceptedDeviationPercentage?: number;
  shouldEnhanceSuggestions?: boolean;
  shouldIncludeHiddenProducts?: boolean;
};

export class ProductClient extends ApiClient {
  private readonly _shopifyProductsGqlToken: string = '';
  private readonly _shopifyProductsGqlUrl: string = '';
  private _algoliaSearchClient: Algoliasearch;
  private _algoliaSearchIndex: string;
  private _recommendationsEndpoint: string;
  private _googleSearchEndpoint: string;
  private _googleSearchApiKey = '';
  private readonly _shouldExcludeMarketplaceProductsFromSuggestions: boolean;
  private readonly _shouldUseBrowseApi: boolean = false;
  private _isBatchingEnabled = false;
  private _batchedQueriesWaitTimeMs = 0;
  private _batchedSkuRequests: Array<{
    skus: Array<string>;
    resolve: (value: ApiResponse<GetProductsResponse> | PromiseLike<ApiResponse<GetProductsResponse>>) => void;
    reject: (reason?: any) => void;
  }>;

  constructor({
    shopifyProductsGqlUrl,
    shopifyProductsGqlToken,
    algoliaAppId,
    algoliaSearchApiKey,
    algoliaSearchIndex,
    recommendationsEndpoint,
    googleSearchEndpoint,
    googleSearchApiKey,
    shouldExcludeMarketplaceProductsFromSuggestions = false,
    shouldUseBrowseApi = false,
    batchedQueriesWaitTimeMs = 0,
  }: ProductClientConstructorArgs) {
    super();
    this._shopifyProductsGqlToken = shopifyProductsGqlToken;
    this._shopifyProductsGqlUrl = shopifyProductsGqlUrl;
    this._algoliaSearchClient = algoliasearch(algoliaAppId, algoliaSearchApiKey, { requester: createFetchRequester() });
    this._algoliaSearchIndex = algoliaSearchIndex;
    this._recommendationsEndpoint = recommendationsEndpoint ?? '';
    this._googleSearchEndpoint = googleSearchEndpoint ?? '';
    this._googleSearchApiKey = googleSearchApiKey ?? '';
    this._shouldExcludeMarketplaceProductsFromSuggestions = shouldExcludeMarketplaceProductsFromSuggestions;
    this._shouldUseBrowseApi = shouldUseBrowseApi;
    this._isBatchingEnabled = batchedQueriesWaitTimeMs > 0;
    this._batchedQueriesWaitTimeMs = batchedQueriesWaitTimeMs;
    this._batchedSkuRequests = [];
    setTimeout(() => (this._isBatchingEnabled = false), STOP_BATCHING_AFTER_MS);
  }

  private async _handleError(message: string, extraErrorProperties: Record<string, string> = {}) {
    return await Promise.reject({ error: { message, ...extraErrorProperties }, statusCode: 500 });
  }

  parseAlgoliaHit = (hit: any): AlgoliaProductHit => {
    return {
      ...hit,
      title: hit.title ?? '',
      product_image: hit.product_image ?? '',
      primary_title: hit.primary_title ?? '',
      subtitle: hit.subtitle ?? '',
      price: hit.price ?? '',
      pricing: {
        ...(hit.pricing ?? {}),
        displayWasPrice: hit.pricing?.displayWasPrice ?? false,
        wasPrice: hit.pricing?.wasPrice ?? '',
      },
      availability: {
        ...(hit.availability ?? {}),
        canPreOrder: hit.availability?.canPreOrder ?? false,
        fulfilment: hit.availability?.fulfilment ?? '',
        productLifecycle: hit.availability?.productLifecycle ?? '',
      },
      product: {
        ...(hit.product ?? {}),
        freightPrice: hit.product?.freightPrice ?? '',
        productGroupId: hit.productGroup?.id ?? '',
      },
    };
  };

  async getSkusFromVariantIds(variantIds: Array<string>): Promise<ApiResponse<GetSkusResponse>> {
    const normalisedVariantIds = variantIds
      ?.filter(Boolean)
      .map(id => (`${id}`.match(/\D/g) ? id : `gid://shopify/ProductVariant/${id}`));
    const QUERY = `{
      nodes (ids: ${JSON.stringify(normalisedVariantIds)}) {
         id,
        ...on ProductVariant { 
          sku
        }
      }
     }`;
    try {
      const response = await fetch(this._shopifyProductsGqlUrl, {
        method: 'POST',
        headers: {
          'X-Shopify-Storefront-Access-Token': this._shopifyProductsGqlToken ?? '',
          'Content-Type': 'application/graphql',
        },
        body: QUERY,
      });
      const result = await response.json();
      if (result?.errors || !result.data) {
        return { error: { message: result.errors?.[0]?.message }, statusCode: 500 };
      }
      return {
        data: { products: result.data?.nodes?.filter((node: { id: string; sku: string } | null) => node?.sku) || [] },
        statusCode: 200,
      };
    } catch (err) {
      return {
        error: { message: typeof err === 'string' ? err : (err?.toString() ?? 'Unknown error') },
        statusCode: 500,
      };
    }
  }

  async getProductsFromVariantIds(
    variantIds: Array<string>,
    algoliaSearchOptions: AlgoliaSearchOptions = {},
    options: GetProductsOptions = {},
    isUseSearchApiEnabled = false
  ): Promise<ApiResponse<GetProductsResponse>> {
    try {
      const getSkusResponse = await this.getSkusFromVariantIds(variantIds);
      if (getSkusResponse.error || !getSkusResponse.data?.products?.filter(Boolean).length) {
        return await Promise.reject(getSkusResponse);
      }

      const productSkus = getSkusResponse.data.products;

      const { data, error, statusCode } = await this.getProductsFromSkus(
        productSkus.map(({ sku }) => sku),
        algoliaSearchOptions,
        options,
        isUseSearchApiEnabled
      );
      if (error || !data?.products?.filter(Boolean).length) {
        return await Promise.reject({ data, error, statusCode });
      }

      return {
        data: {
          ...data,
          products: data.products?.map(product => ({
            ...product,
            global_variant_id: productSkus.find(({ sku }) => sku === product.sku)?.id,
          })),
        },
        error,
        statusCode,
      };
    } catch (e) {
      return await this._handleError(
        `Error fetching products from algolia with variant IDs: ${JSON.stringify(variantIds)}`,
        { originalError: JSON.stringify(e) }
      );
    }
  }

  async getProductsFromAlgoliaQuery(
    query: string,
    algoliaSearchOptions: AlgoliaSearchOptions = {},
    options: GetProductsOptions = {}
  ): Promise<ApiResponse<GetProductsResponse>> {
    try {
      const algoliaIndex = `${this._algoliaSearchIndex}${options.sortByMethod ? `_${options.sortByMethod}` : ''}`;
      const result = await this._algoliaSearchClient.searchSingleIndex({
        indexName: algoliaIndex,
        searchParams: {
          ...algoliaSearchOptions,
          filters: buildSearchFilters(query, {
            shouldExcludeMarketplaceProducts: options.shouldExcludeMarketplaceProducts,
          }),
        },
      });

      const algoliaProductHits = result?.hits?.filter(Boolean)?.map(this.parseAlgoliaHit);

      if (algoliaProductHits) {
        return {
          data: {
            products: options.shouldResolveExtraOnlineProductFields
              ? await this.resolveExtraOnlineProductFields(algoliaProductHits)
              : algoliaProductHits,
            numberOfProducts: result?.nbHits ?? 0,
          },
          statusCode: 200,
        };
      }
      return await this._handleError('Error fetching products from algolia with search filter');
    } catch (err) {
      return await this._handleError('Error fetching products from algolia with search filter');
    }
  }

  private async _processBatchedSkuRequests() {
    if (!this._batchedSkuRequests.length) return;

    const batchedRequests = [...this._batchedSkuRequests];
    this._batchedSkuRequests = [];

    try {
      const groupedSkus = batchedRequests.flatMap(request => request.skus);
      const response = await this.getProductsFromSkusNow(groupedSkus);

      for (const request of batchedRequests) {
        const { skus, resolve } = request;
        const products = skus
          .map(sku => response?.data?.products.find(product => [product.sku, ...(product?.skus ?? [])].includes(sku)))
          ?.filter((hit): hit is NonNullable<typeof hit> => !!hit);

        resolve({ data: { products }, statusCode: response.statusCode, error: response.error });
      }
    } catch (err) {
      for (const request of batchedRequests) {
        request.reject({ error: { message: `Error processing batched requests: ${err}` }, statusCode: 500 });
      }
    }
  }

  async getProductsFromSkus(
    skus: Array<string>,
    algoliaSearchOptions: AlgoliaSearchOptions = {},
    options: GetProductsOptions = {},
    isUseSearchApiEnabled = false
  ): Promise<ApiResponse<GetProductsResponse>> {
    const isEligibleForBatching =
      /* Only batch 'basic' requests without modifier options */
      this._isBatchingEnabled &&
      !Object.values(algoliaSearchOptions).some(Boolean) &&
      !Object.values(options).some(Boolean);

    if (!isEligibleForBatching) {
      return await this.getProductsFromSkusNow(skus, algoliaSearchOptions, options, isUseSearchApiEnabled);
    }

    if (!this._batchedSkuRequests.length) {
      setTimeout(() => this._processBatchedSkuRequests(), this._batchedQueriesWaitTimeMs);
    }
    return await new Promise((resolve, reject) => {
      this._batchedSkuRequests.push({
        skus,
        resolve,
        reject,
      });
    });
  }

  async getProductsFromSkusNow(
    skus: Array<string>,
    algoliaSearchOptions: AlgoliaSearchOptions = {},
    options: GetProductsOptions = {},
    isUseSearchApiEnabled = false
  ): Promise<ApiResponse<GetProductsResponse>> {
    try {
      if (!skus?.length) {
        return { data: { products: [] }, statusCode: 200 };
      }
      const uniqueSkus = [...new Set(skus?.filter(Boolean))].map(sku => `(sku:${sku} OR skus:${sku})`);
      const searchParameters: SearchParamsObject = {
        hitsPerPage: Math.max(uniqueSkus.length, 20),
        ...algoliaSearchOptions,
        filters: buildSearchFilters(uniqueSkus.join(' OR '), {
          shouldExcludeMarketplaceProducts: options?.shouldExcludeMarketplaceProducts,
          shouldIncludeHiddenProducts: options?.shouldIncludeHiddenProducts,
        }),
        analytics: true,
        ...(options.analyticsTags?.length && {
          analyticsTags: options.analyticsTags,
        }),
      };
      const result = await (this._shouldUseBrowseApi && !isUseSearchApiEnabled
        ? this._algoliaSearchClient.browse({
            indexName: this._algoliaSearchIndex,
            browseParams: searchParameters,
          })
        : this._algoliaSearchClient.searchSingleIndex({
            indexName: this._algoliaSearchIndex,
            searchParams: searchParameters,
          }));

      const algoliaProductHits = result?.hits?.filter(Boolean).map(this.parseAlgoliaHit);

      if (algoliaProductHits) {
        const products = skus
          .map(sku => algoliaProductHits.find(hit => hit.sku === sku || hit.skus?.includes(sku)))
          .filter(Boolean);
        return {
          data: {
            products: options.shouldResolveExtraOnlineProductFields
              ? await this.resolveExtraOnlineProductFields(products)
              : products,
            numberOfProducts: result?.nbHits,
          },
          statusCode: 200,
        };
      }

      return await this._handleError('Error fetching products from algolia with skus');
    } catch (err) {
      return await this._handleError(`Error fetching products from algolia with skus: ${err}`);
    }
  }

  async getProductRecommendations({
    productSkus,
    modelType = 'RFY',
    source = 'pdp',
    numberOfResults = 12,
    visitorId,
    algoliaSearchOptions = {},
    options = {
      acceptedDeviationPercentage: 20,
      shouldEnhanceSuggestions: false,
    },
    excludedSkus,
  }: {
    productSkus?: Array<string>;
    modelType?: string;
    source?: string;
    numberOfResults?: number;
    visitorId: string;
    algoliaSearchOptions?: AlgoliaSearchOptions;
    options?: GetProductsOptions;
    excludedSkus?: Array<string>;
  }): Promise<ApiResponse<GetProductsResponse>> {
    const { data, error, statusCode } = await this._fetch<{
      results: Array<{ id: string; itemMetadata: { score: number } }>;
    }>({
      path: this._recommendationsEndpoint,
      method: 'POST',
      body: {
        placement: modelType,
        visitor_id: visitorId || null,
        event_type: source === 'pdp' ? 'detail-page-view' : 'home-page-view',
        page_size: numberOfResults,
        product_ids: productSkus,
        validate_only: false,
        return_product: false,
        return_score: true,
      },
    });

    if (error || !data?.results?.length) {
      return { error: error ?? { message: 'getProductRecommendations error' }, statusCode: statusCode };
    }

    const filteredData = options?.shouldEnhanceSuggestions
      ? filterGoogleRecommendations(data?.results, options.acceptedDeviationPercentage)
      : (data?.results as Array<GoogleRecommendedItemWithVariance>);

    const skus = filteredData.map(product => product.id);

    const productsResponse = await this.getProductsFromSkus(
      [...skus, ...(excludedSkus ?? [])],
      { ...algoliaSearchOptions, hitsPerPage: numberOfResults },
      { shouldExcludeMarketplaceProducts: this._shouldExcludeMarketplaceProductsFromSuggestions, ...options }
    );

    return {
      ...productsResponse,
      data: {
        ...productsResponse.data,
        products: (productsResponse.data?.products ?? []).map(product => {
          const matchedGoogleRecommendation = filteredData.find(
            recommendedProduct => recommendedProduct.id === product.sku
          );

          return {
            ...product,
            variance: matchedGoogleRecommendation?.variance,
            varianceScore: matchedGoogleRecommendation?.varianceScore,
            googleScore: matchedGoogleRecommendation?.itemMetadata.score,
          } as ScoredAlgoliaHit;
        }),
        numberOfProducts: filteredData.length,
      },
    };
  }

  async getGoogleSearchResults({
    googleSearchQuery,
    visitorId,
  }: GoogleSearchArgs): Promise<ApiResponse<GoogleSearchResponse>> {
    const query = googleSearchQuery?.trim();
    const vid = visitorId?.trim();
    if (!query || !vid) {
      return await this._handleError('Missing googleSearchQuery or visitorId');
    }

    const response = await fetch(this._googleSearchEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': this._googleSearchApiKey,
      },
      body: JSON.stringify({ visitor_id: vid, query, page_size: 100 }),
    });
    const result = await response.json();
    if (result?.error || !result?.results) {
      return {
        error: { message: result?.error?.message ?? 'Error fetching products from google' },
        statusCode: result?.error?.code ?? 500,
      };
    }
    return { data: result, statusCode: 200 };
  }

  async resolveExtraOnlineProductFields(products: Array<AlgoliaProductHit>): Promise<Array<AlgoliaProductHit>> {
    const normalisedVariantIds = products
      ?.filter(p => p.variant_id)
      .map(({ variant_id }) =>
        `${variant_id}`.match(/\D/g) ? variant_id : `gid://shopify/ProductVariant/${variant_id}`
      );

    const onlineProductQuery = `{
      nodes(ids: ${JSON.stringify(normalisedVariantIds)}){
        ...on ProductVariant { 
          product {
            metafield (namespace: "online_product", key: "value"){
              value
            }
          }
        }
      }
    }`;

    try {
      const response = await fetch(this._shopifyProductsGqlUrl, {
        method: 'POST',
        headers: {
          'X-Shopify-Storefront-Access-Token': this._shopifyProductsGqlToken ?? '',
          'Content-Type': 'application/graphql',
        },
        body: onlineProductQuery,
      });
      const result = await response.json();
      const onlineProducts = (
        (result?.data?.nodes || []) as Array<{
          product: { metafield: { value: string } };
        }>
      ).map(item => toCamelCase(JSON.parse(item.product.metafield.value)));
      return products.map(product => {
        const onlineProduct = onlineProducts.find(p => p.id === product.sku || product.skus?.includes(p.id)) as
          | {
              category: AlgoliaProductHit['onlineProductCategory'];
              display?: { relatedProductIds?: Array<string> };
            }
          | undefined;
        return {
          ...product,
          onlineProductCategory: onlineProduct?.category,
          relatedProductSkus: onlineProduct?.display?.relatedProductIds || [],
        };
      });
    } catch (err) {
      return products;
    }
  }

  static getAlgoliaCache = (appId: string, durationInSeconds = 60) => {
    return {
      responsesCache: createFallbackableCache({
        caches: [
          createBrowserLocalStorageCache({
            key: `algolia-responses-${appId}`,
            timeToLive: durationInSeconds,
          }),
          createInMemoryCache(),
        ],
      }),
    };
  };
}
